import React from 'react';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function getSessionIssueLabel(reflections) {
  if (!Array.isArray(reflections) || reflections.length === 0) return '';
  const row = reflections.find((r) => String(r?.issue_title || r?.issue_id || '').trim().length > 0);
  if (!row) return '';
  return String(row.issue_title || row.issue_id || '').trim();
}

function formatDuration(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${String(secs).padStart(2, '0')}s`;
  }

  return `${minutes}m ${String(secs).padStart(2, '0')}s`;
}

function renderStatus(status) {
  const cls = `pomodoro-session-status pomodoro-session-status--${status || 'unknown'}`;
  const label =
    status === 'completed' ? 'Completed'
      : status === 'abandoned' ? 'Abandoned'
      : status === 'writing' ? 'In progress'
      : status || '—';
  return <span className={cls}>{label}</span>;
}

function renderClientSource(source) {
  const normalized = source === 'mobile' ? 'mobile' : 'web';
  const label = normalized === 'mobile' ? 'Mobile' : 'Web';
  return (
    <span className={`pomodoro-session-source pomodoro-session-source--${normalized}`}>
      {label}
    </span>
  );
}

function renderDifficultyScore(score) {
  const numeric = Number(score);
  if (!numeric) return '—';
  const filled = '★'.repeat(numeric);
  const empty = '☆'.repeat(5 - numeric);
  return `${filled}${empty} (${numeric}/5)`;
}

function formatExcerpt(value, maxCharacters = 96) {
  const text = String(value || '').trim();
  if (text.length <= maxCharacters) return text;
  return `${text.slice(0, maxCharacters).trimEnd()}…`;
}

function getReflectionHistoryEntries(reflection) {
  const rows = Array.isArray(reflection?.question_history) ? reflection.question_history : [];
  const sortedRows = rows.slice().sort((a, b) => {
    const ai = Number(a?.question_index ?? 0);
    const bi = Number(b?.question_index ?? 0);
    if (ai !== bi) return ai - bi;
    return new Date(String(a?.created_at || 0)).getTime() - new Date(String(b?.created_at || 0)).getTime();
  });

  const grouped = new Map();

  for (const row of sortedRows) {
    const index = Number(row?.question_index ?? grouped.size);
    const existing = grouped.get(index) || {
      index,
      question_label: String(row?.question_label || 'Question').trim() || 'Question',
      question: '',
      answer: '',
      created_at: row?.created_at || null,
      answered_at: row?.answered_at || null,
    };

    const rowQuestion = String(row?.resolved_question || row?.generated_question || row?.base_question || '').trim();
    const rowAnswer = String(row?.answer_text || '').trim();

    if (rowQuestion && (!existing.question || row?.generated_question || row?.source === 'generated')) {
      existing.question = rowQuestion;
    }

    if (rowAnswer) {
      existing.answer = rowAnswer;
    }

    if (!existing.created_at && row?.created_at) {
      existing.created_at = row.created_at;
    }

    if (!existing.answered_at && row?.answered_at) {
      existing.answered_at = row.answered_at;
    }

    grouped.set(index, existing);
  }

  return Array.from(grouped.values()).sort((a, b) => a.index - b.index);
}

function getFirstAnswerExcerpt(reflections) {
  for (const reflection of reflections) {
    const historyAnswer = getReflectionHistoryEntries(reflection)
      .find((item) => String(item?.answer || '').trim())?.answer;
    if (historyAnswer) return formatExcerpt(historyAnswer);

    const legacyAnswer = reflection?.legacy_fields
      ?.find((field) => String(field?.value || '').trim())?.value;
    const fallbackAnswer = legacyAnswer
      || reflection?.accomplished
      || reflection?.blocker
      || reflection?.next_action
      || reflection?.difficulty_reason
      || reflection?.feelings;
    if (fallbackAnswer) return formatExcerpt(fallbackAnswer);
  }

  return '';
}

export default function PomodoroSessionCard({
  session,
  reflections = [],
  isExpanded = false,
  onToggleExpanded,
  onContinueSession,
  onDeleteSession,
  onPublishToggle,
  showActions = false,
  loadingReflections = false,
  reflectionOnly = false,
  titleOverride = "",
  groupedIssue = false,
  publicFeed = false,
  useAnswerExcerptAsTitle = false,
}) {
  const sessionReflections = Array.isArray(reflections) ? reflections : [];
  const hasReflections = sessionReflections.length > 0;
  const issueLabel = getSessionIssueLabel(sessionReflections);
  const answerExcerpt = (publicFeed || useAnswerExcerptAsTitle) ? getFirstAnswerExcerpt(sessionReflections) : '';
  const primaryReflection = sessionReflections[0] || null;
  const isPublished = primaryReflection?.status === 'published';

  return (
    <li className={`pomodoro-session-row ${isExpanded ? 'pomodoro-session-row--expanded' : ''}${groupedIssue ? ' pomodoro-session-row--grouped' : ''}${publicFeed ? ' pomodoro-session-row--public-feed' : ''}`}>
      {!reflectionOnly && <div className="pomodoro-session-row-main">
        {(publicFeed || useAnswerExcerptAsTitle) ? (
          <>
            <div className="pomodoro-session-feed-row">
              <span className="pomodoro-session-title pomodoro-session-answer-excerpt">{answerExcerpt || titleOverride || ''}</span>
              <button
                type="button"
                className="pomodoro-session-reflection-btn pomodoro-session-reflection-btn--badge"
                onClick={() => onToggleExpanded?.(session?.id)}
                aria-label={isExpanded ? 'Hide reflection' : 'Show reflection'}
                title={isExpanded ? 'Hide reflection' : 'Show reflection'}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="pomodoro-session-heading">
              <span className="pomodoro-session-title">{(useAnswerExcerptAsTitle && answerExcerpt) || titleOverride || (issueLabel ? `Task: ${issueLabel}` : 'Pomodoro session')}</span>
            </div>
            <div className="pomodoro-session-reflection-control">
              <button
                type="button"
                className="pomodoro-session-reflection-btn pomodoro-session-reflection-btn--badge"
                onClick={() => onToggleExpanded?.(session?.id)}
                aria-label={isExpanded ? 'Hide reflection' : 'Show reflection'}
                title={isExpanded ? 'Hide reflection' : 'Show reflection'}
              >
                {isExpanded ? '▼' : '▶'} Reflection
              </button>
            </div>
          </>
        )}
      </div>}

      {showActions && !reflectionOnly && (
        <div className="pomodoro-session-row-actions">
          {session?.status === 'abandoned' && (
            <button
              type="button"
              className="pomodoro-session-continue-btn"
              onClick={() => onContinueSession?.(session)}
              aria-label="Continue session"
              title="Continue this session"
            >
              ▶ Continue
            </button>
          )}
          {hasReflections && primaryReflection && (
            isPublished ? (
              <button
                type="button"
                className="pomodoro-session-unpublish-btn"
                onClick={() => onPublishToggle?.(session?.id, primaryReflection.id, 'private')}
                aria-label="Unpublish reflection"
                title="Remove this reflection from your public feed"
              >
                Unpublish
              </button>
            ) : (
              <button
                type="button"
                className="pomodoro-session-publish-btn"
                onClick={() => onPublishToggle?.(session?.id, primaryReflection.id, 'published')}
                aria-label="Publish reflection"
                title="Show this reflection on your public feed"
              >
                Publish
              </button>
            )
          )}
          <button
            type="button"
            className="pomodoro-session-delete"
            onClick={() => onDeleteSession?.(session?.id)}
            aria-label="Delete session"
            title="Delete session"
          >
            ×
          </button>
        </div>
      )}

      {(isExpanded || reflectionOnly) && (
        <div className="pomodoro-session-reflection-panel">
          {loadingReflections ? (
            <div className="pomodoro-reflection-loading">Loading reflections…</div>
          ) : !hasReflections ? (
            <div className="pomodoro-reflection-empty">No reflections recorded for this session.</div>
          ) : (
            <div className="pomodoro-reflection-list">
              {sessionReflections
                .map((reflection, index) => {
                  const questionHistory = getReflectionHistoryEntries(reflection);
                  const legacyFields = Array.isArray(reflection?.legacy_fields) ? reflection.legacy_fields : [];
                  const legacyAnswers = legacyFields.filter((field) => String(field?.value || '').trim().length > 0);
                  const hasAnyAnswer =
                    questionHistory.length > 0 ||
                    legacyAnswers.length > 0 ||
                    reflection.accomplished ||
                    reflection.blocker ||
                    reflection.next_action ||
                    reflection.difficulty_score ||
                    reflection.feelings;

                  if (!hasAnyAnswer) return null;

                  const number = sessionReflections.length - index;
                  return (
                    <div key={reflection?.id || `${session?.id || 'session'}-r-${index}`} className="pomodoro-reflection-details">
                      {!publicFeed && !useAnswerExcerptAsTitle && (
                        <h4 className="pomodoro-reflection-title">
                          {sessionReflections.length > 1 ? `Reflection ${number}` : 'Reflection'}
                        </h4>
                      )}

                      <details className="pomodoro-reflection-issue-collapsible" open>
                        <summary className="pomodoro-reflection-issue-summary">
                          <span className="pomodoro-reflection-issue-chip">Task description</span>
                          <span className="pomodoro-reflection-issue-summary-details">
                            <span className="pomodoro-reflection-issue-summary-text">
                              {reflection?.issue_description || 'No description available'}
                            </span>
                            {!publicFeed && (
                              <span className="pomodoro-reflection-issue-time">
                                Time spent · {formatDuration(session?.actual_writing_seconds)}
                              </span>
                            )}
                          </span>
                          <span className="pomodoro-reflection-issue-chevron" aria-hidden="true">▸</span>
                        </summary>

                        <div className="pomodoro-reflection-issue-content">
                          <div className="pomodoro-session-row-meta pomodoro-session-row-meta--inline pomodoro-reflection-meta">
                            <span className="pomodoro-session-meta-pill">{formatDate(session?.started_at)}</span>
                            {session?.chapter_title && (
                              <span className="pomodoro-session-meta-pill pomodoro-session-meta-pill--chapter">{session.chapter_title}</span>
                            )}
                            {renderClientSource(session?.client_source)}
                            {renderStatus(session?.status)}
                          </div>

                          <div className="pomodoro-reflection-issue-indent">
                            {questionHistory.length > 0 && questionHistory.some((item) => String(item?.answer || item?.answer_text || '').trim().length > 0) ? (
                              questionHistory.map((item, idx) => {
                                const question = String(item?.question || '').trim()
                                  || String(item?.resolved_question || '').trim()
                                  || String(item?.generated_question || '').trim()
                                  || String(item?.base_question || '').trim()
                                  || `Question ${idx + 1}`;
                                const answer = String(item?.answer || item?.answer_text || '').trim();
                                if (!answer) return null;

                                return (
                                  <div key={item?.id || `${reflection?.id || 'reflection'}-q-${idx}`} className="pomodoro-reflection-item pomodoro-reflection-item--qa">
                                    <div className="pomodoro-reflection-qa-block pomodoro-reflection-qa-block--question">
                                      <span className="pomodoro-reflection-label">Question</span>
                                      <p className="pomodoro-reflection-qa-text pomodoro-reflection-question">{question}</p>
                                    </div>
                                    <div className="pomodoro-reflection-qa-block pomodoro-reflection-qa-block--answer">
                                      <span className="pomodoro-reflection-label">Answer</span>
                                      <p className="pomodoro-reflection-qa-text">{answer}</p>
                                    </div>
                                  </div>
                                );
                              })
                            ) : legacyAnswers.length > 0 ? (
                              <>
                                {legacyAnswers.map((field) => {
                                  const label = String(field?.label || '').trim();
                                  const value = String(field?.value || '').trim();
                                  if (!label || !value) return null;
                                  return (
                                    <div key={`${session?.id || 'session'}-legacy-${field?.key || label}`} className="pomodoro-reflection-item">
                                      <span className="pomodoro-reflection-label">{`${label}:`}</span>
                                      <span className="pomodoro-reflection-value">{value}</span>
                                    </div>
                                  );
                                })}
                              </>
                            ) : (
                              <>
                                {reflection.accomplished && (
                                  <div className="pomodoro-reflection-item">
                                    <span className="pomodoro-reflection-label">Accomplished:</span>
                                    <span className="pomodoro-reflection-value">{reflection.accomplished}</span>
                                  </div>
                                )}

                                {reflection.blocker && (
                                  <div className="pomodoro-reflection-item">
                                    <span className="pomodoro-reflection-label">Blocker:</span>
                                    <span className="pomodoro-reflection-value">{reflection.blocker}</span>
                                  </div>
                                )}

                                {reflection.next_action && (
                                  <div className="pomodoro-reflection-item">
                                    <span className="pomodoro-reflection-label">Next action:</span>
                                    <span className="pomodoro-reflection-value">{reflection.next_action}</span>
                                  </div>
                                )}

                                {reflection.difficulty_score && (
                                  <div className="pomodoro-reflection-item">
                                    <span className="pomodoro-reflection-label">Difficulty:</span>
                                    <span className="pomodoro-reflection-value">{renderDifficultyScore(reflection.difficulty_score)}</span>
                                  </div>
                                )}

                                {reflection.difficulty_reason && (
                                  <div className="pomodoro-reflection-item">
                                    <span className="pomodoro-reflection-label">Why:</span>
                                    <span className="pomodoro-reflection-value">{reflection.difficulty_reason}</span>
                                  </div>
                                )}

                                {reflection.feelings && (
                                  <div className="pomodoro-reflection-item">
                                    <span className="pomodoro-reflection-label">Feelings:</span>
                                    <span className="pomodoro-reflection-value">{reflection.feelings}</span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </details>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
    </li>
  );
}